import React, { useRef } from 'react';
import { heroFeatures, IMAGES } from '../mock';
import { Droplet, ShieldCheck, Award, Leaf, ArrowRight } from 'lucide-react';
import BottleCarousel from './BottleCarousel';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal, TextReveal } from './Reveal';

const iconMap = { Droplet, ShieldCheck, Award, Leaf };

const Hero = () => {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 200]);
  const bottleY = useTransform(scrollY, [0, 800], [0, 120]);
  const textY = useTransform(scrollY, [0, 800], [0, -50]);
  const bgOpacity = useTransform(scrollY, [0, 600], [1, 0.4]);

  const handleClick = (href) => {
    const el = document.querySelector(href);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-[100vh] pt-28 pb-16 lg:pt-32 lg:pb-24 overflow-hidden bg-gradient-to-b from-[#f4f8ff] via-[#eaf2ff] to-[#fafaf7]"
    >
      {/* Parallax mountain background */}
      <motion.div
        style={{ y: bgY, opacity: bgOpacity }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.mountainBg}
          alt="Mountain landscape"
          className="w-full h-[120%] object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-[#fafaf7]" />
      </motion.div>

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      {/* Decorative shapes */}
      <div className="absolute top-24 -left-20 w-96 h-96 bg-white/50 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 right-0 w-[500px] h-[500px] bg-sky-100/40 rounded-full blur-3xl" />

      {/* Vertical decorative label */}
      <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 vertical-text text-[10px] tracking-[0.5em] text-[#0f2c6b]/50 font-medium">
        EST. INDIA · REGISTERED DESIGN
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-[75vh]">
          {/* Left: Text */}
          <motion.div style={{ y: textY }} className="lg:col-span-5 z-10">
            <Reveal delay={0.1}>
              <div className="inline-flex items-center gap-2 mb-6 px-3.5 py-1.5 bg-white/70 backdrop-blur-sm border border-[#c4a24a]/40 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#c4a24a] animate-pulse" />
                <span className="text-[10px] tracking-[0.28em] font-medium text-[#0f2c6b] uppercase">
                  A registered design of India
                </span>
              </div>
            </Reveal>

            <h1
              className="text-[46px] leading-[1.02] sm:text-[58px] lg:text-[72px] xl:text-[88px] font-medium text-[#0f2c6b] text-balance"
              style={{
                fontFamily: 'Fraunces, Playfair Display, serif',
                fontVariationSettings: '"opsz" 144',
                letterSpacing: '-0.03em',
              }}
            >
              <div className="overflow-hidden">
                <TextReveal text="Every drop." delay={0.15} />
              </div>
              <div className="overflow-hidden">
                <TextReveal text="Pure. Protected." delay={0.3} />
              </div>
              <div className="overflow-hidden">
                <span className="italic font-light text-[#c4a24a]">
                  <TextReveal text="Premium." delay={0.5} />
                </span>
              </div>
            </h1>

            <Reveal delay={0.8}>
              <div className="mt-8 flex items-start gap-4 max-w-md">
                <div className="w-8 h-[1px] bg-[#c4a24a] mt-3 shrink-0" />
                <p className="text-[14.5px] leading-[1.75] text-slate-600 font-light">
                  Experience the purity of Tropic Blue® Premium Packaged Drinking Water, crafted through advanced purification technology and delivered in our exclusive registered bottle design.
                </p>
              </div>
            </Reveal>

            <Reveal delay={1.0}>
              <div className="mt-10 flex flex-wrap gap-3">
                <button
                  onClick={() => handleClick('#products')}
                  className="premium-button group bg-[#0f2c6b] hover:bg-[#1e3a8a] text-white text-[11.5px] font-medium tracking-[0.22em] px-7 py-4 flex items-center gap-3 transition-all duration-300"
                >
                  EXPLORE PRODUCTS
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <button
                  onClick={() => handleClick('#distributor')}
                  className="group text-[#0f2c6b] text-[11.5px] font-medium tracking-[0.22em] px-7 py-4 border-b border-[#0f2c6b] hover:border-[#c4a24a] hover:text-[#c4a24a] transition-colors duration-300 flex items-center gap-3"
                >
                  BECOME A DISTRIBUTOR
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </Reveal>
          </motion.div>

          {/* Middle: Auto-cycling bottle carousel */}
          <div className="lg:col-span-5 flex justify-center relative">
            <motion.div style={{ y: bottleY }} className="relative">
              {/* Halo glow */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.div
                  animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-[440px] h-[440px] rounded-full bg-gradient-radial from-sky-200/70 via-sky-100/30 to-transparent blur-3xl"
                />
              </div>
              {/* Gold ring accent */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div
                  className="w-[460px] h-[460px] rounded-full border border-dashed"
                  style={{ borderColor: 'rgba(196, 162, 74, 0.35)' }}
                />
              </motion.div>
              {/* Splash droplets */}
              <SplashDroplets />
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <BottleCarousel />
              </motion.div>
            </motion.div>
          </div>

          {/* Right: Feature Icons */}
          <div className="lg:col-span-2 flex lg:flex-col justify-around lg:justify-center gap-6 lg:gap-8">
            {heroFeatures.map((f, idx) => {
              const Icon = iconMap[f.icon];
              return (
                <Reveal key={idx} delay={1.2 + idx * 0.15} y={20}>
                  <div className="flex flex-col items-center text-center group cursor-default">
                    <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full border border-[#c4a24a]/30 flex items-center justify-center text-[#0f2c6b] bg-white/60 backdrop-blur-sm group-hover:border-[#c4a24a] group-hover:bg-white group-hover:scale-110 group-hover:-translate-y-1 transition-all duration-500">
                      <Icon size={28} strokeWidth={1.3} />
                    </div>
                    <div className="mt-3 text-[10px] lg:text-[10.5px] font-medium tracking-[0.15em] text-slate-600 whitespace-pre-line leading-tight">
                      {f.label}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#0f2c6b]/50"
        >
          <span className="text-[9px] tracking-[0.4em] font-medium">SCROLL</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-[1px] h-8 bg-gradient-to-b from-[#0f2c6b]/50 to-transparent"
          />
        </motion.div>
      </div>
    </section>
  );
};

const SplashDroplets = () => (
  <>
    <motion.div
      animate={{ y: [0, -8, 0], opacity: [0.4, 0.9, 0.4] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute -top-4 left-4 w-3 h-3 rounded-full bg-sky-200"
    />
    <motion.div
      animate={{ y: [0, -6, 0], opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 3.5, repeat: Infinity, delay: 0.3, ease: 'easeInOut' }}
      className="absolute top-10 -right-4 w-4 h-4 rounded-full bg-sky-100"
    />
    <motion.div
      animate={{ y: [0, 6, 0], opacity: [0.4, 0.8, 0.4] }}
      transition={{ duration: 4, repeat: Infinity, delay: 0.6, ease: 'easeInOut' }}
      className="absolute bottom-16 -left-6 w-5 h-5 rounded-full bg-sky-200/70"
    />
    <motion.div
      animate={{ y: [0, -4, 0], opacity: [0.5, 0.9, 0.5] }}
      transition={{ duration: 3.2, repeat: Infinity, delay: 0.9, ease: 'easeInOut' }}
      className="absolute bottom-4 right-8 w-3 h-3 rounded-full bg-sky-100"
    />
    <motion.div
      animate={{ y: [0, -5, 0], opacity: [0.3, 0.7, 0.3] }}
      transition={{ duration: 3.8, repeat: Infinity, delay: 1.2, ease: 'easeInOut' }}
      className="absolute top-24 -left-8 w-2 h-2 rounded-full bg-sky-200"
    />
  </>
);

const WaterSplash = () => (
  <svg
    viewBox="0 0 500 500"
    className="absolute inset-0 w-[140%] h-[140%] -left-[20%] -top-[20%] pointer-events-none animate-drift"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="splashGradPremium" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#dbeafe" stopOpacity="0.7" />
        <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.55" />
      </linearGradient>
    </defs>
    <path d="M 120 180 Q 90 150 100 120 Q 108 140 128 158 Q 118 176 120 180 Z" fill="url(#splashGradPremium)" />
    <path d="M 80 260 Q 40 240 50 200 Q 70 230 100 250 Q 90 258 80 260 Z" fill="url(#splashGradPremium)" />
    <path d="M 100 320 Q 60 340 40 320 Q 70 300 110 310 Q 108 316 100 320 Z" fill="url(#splashGradPremium)" />
    <path d="M 380 150 Q 420 130 430 100 Q 410 130 385 148 Z" fill="url(#splashGradPremium)" />
    <path d="M 400 240 Q 460 220 460 180 Q 430 220 400 238 Z" fill="url(#splashGradPremium)" />
    <path d="M 390 320 Q 440 340 460 310 Q 420 310 390 315 Z" fill="url(#splashGradPremium)" />
    <circle cx="60" cy="180" r="6" fill="#bfdbfe" opacity="0.85" />
    <circle cx="440" cy="200" r="7" fill="#bfdbfe" opacity="0.85" />
    <circle cx="50" cy="300" r="5" fill="#93c5fd" opacity="0.9" />
    <circle cx="450" cy="280" r="5" fill="#93c5fd" opacity="0.9" />
    <circle cx="90" cy="380" r="4" fill="#bfdbfe" opacity="0.8" />
    <circle cx="410" cy="380" r="4" fill="#bfdbfe" opacity="0.8" />
    <circle cx="130" cy="100" r="4" fill="#dbeafe" opacity="0.85" />
    <circle cx="370" cy="90" r="5" fill="#dbeafe" opacity="0.85" />
    <ellipse cx="250" cy="460" rx="130" ry="18" fill="#334155" opacity="0.28" />
    <path d="M 150 460 Q 180 430 220 435 Q 260 425 310 438 Q 340 435 355 460 Z" fill="#1e293b" opacity="0.45" />
  </svg>
);

export default Hero;
